import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import {
  ArrowRight,
  Award,
  Users,
  Heart,
  Target,
  MapPin,
  Phone,
} from "lucide-react";
import { companyInfo, stats } from "../data/mock";
import { useScrollReveal } from "../hooks/useScrollReveal";

const Section = ({ children, className = "" }) => {
  const [ref, isVisible] = useScrollReveal();
  return (
    <section
      ref={ref}
      className={`reveal ${isVisible ? "visible" : ""} ${className}`}
    >
      {children}
    </section>
  );
};

export default function About() {
  return (
    <div>
      {/* Hero */}
      <section className="bg-slate-50 py-16 lg:py-20">
        <div className="container mx-auto px-6 page-enter">
          <div className="max-w-3xl">
            <Badge
              variant="secondary"
              className="mb-4 px-3 py-1 text-blue-700 bg-blue-50 border border-blue-100 text-xs font-medium uppercase tracking-wider"
            >
              About Us
            </Badge>
            <h1 className="text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight mb-4">
              Your Trusted IT Partner Since 2003
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed">
              FlexiTech was founded with a simple belief: every Australian
              business deserves access to professional, reliable IT support —
              without the enterprise price tag.
            </p>
          </div>
        </div>
      </section>

      {/* Story */}
      <Section className="py-16 lg:py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <h2 className="text-3xl font-bold text-slate-900 tracking-tight mb-6">
                Our Story
              </h2>
              <div className="space-y-4 text-slate-600 leading-relaxed">
                <p>
                  Founded in Sydney in 2003, FlexiTech started as
                  a one-person IT consulting practice helping local medical
                  centres manage their technology. Over two decades, we've grown
                  into a full-service managed IT provider trusted by hundreds of
                  businesses across Australia.
                </p>
                <p>
                  What hasn't changed is our commitment to personal service. We
                  believe technology should be an enabler, not a headache. Our
                  team works as an extension of your business — learning your
                  operations, anticipating your needs, and ensuring your
                  technology just works.
                </p>
                <p>
                  Today, FlexiTech serves small and medium businesses across
                  healthcare, legal, real estate, accounting, and professional
                  services. We're proud that many of our earliest clients are
                  still with us, a testament to the relationships we build and
                  the results we deliver.
                </p>
              </div>
            </div>

            {/* Values */}
            <div className="space-y-5">
              {[
                {
                  icon: Heart,
                  title: "Client-First Approach",
                  desc: "Your success is our success. We measure ourselves by the outcomes we deliver for your business.",
                },
                {
                  icon: Target,
                  title: "Proactive Mindset",
                  desc: "We don't wait for problems. Our proactive approach prevents issues before they impact you.",
                },
                {
                  icon: Users,
                  title: "Real Relationships",
                  desc: "You'll know your account manager by name. We're a partner, not a faceless call centre.",
                },
                {
                  icon: Award,
                  title: "Technical Excellence",
                  desc: "Certified professionals across Microsoft, security, and cloud platforms. Continuous training is in our DNA.",
                },
              ].map((value, i) => (
                <Card key={i} className="border-slate-100">
                  <CardContent className="p-6 flex items-start gap-4">
                    <div className="w-11 h-11 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
                      <value.icon className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-1">
                        {value.title}
                      </h3>
                      <p className="text-sm text-slate-500 leading-relaxed">
                        {value.desc}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </Section>

      {/* Stats */}
      <div className="bg-slate-900 relative grain-overlay">
        <div className="container mx-auto px-6 py-14">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { value: "20+", label: "Years in Business" },
              { value: "500+", label: "Clients Served" },
              { value: "99%", label: "Client Retention" },
              { value: "50,000+", label: "Tickets Resolved" },
            ].map((stat, i) => (
              <div key={i} className="text-center">
                <div className="text-3xl lg:text-4xl font-bold text-white mb-1">
                  {stat.value}
                </div>
                <div className="text-sm text-slate-400 font-medium">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA */}
      <Section className="py-16 lg:py-20 bg-slate-50">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-slate-900 tracking-tight mb-4">
              Let's Work Together
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed mb-8">
              Ready to experience the FlexiTech difference? Get in touch for a
              free, no-obligation chat about your IT needs.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button
                asChild
                size="lg"
                className="bg-blue-600 hover:bg-blue-700 text-white btn-primary h-12 px-8"
              >
                <Link to="/contact">
                  Contact Us
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Link>
              </Button>
              <Button
                asChild
                variant="outline"
                size="lg"
                className="h-12 px-8 border-slate-300 text-slate-700"
              >
                <a href={`tel:${companyInfo.phone.replace(/\s/g, "")}`}>
                  <Phone className="w-4 h-4 mr-2" />
                  {companyInfo.phone}
                </a>
              </Button>
            </div>
          </div>
        </div>
      </Section>
    </div>
  );
}
